import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Gear, Plus, Trash, Database, Buildings, UploadSimple, Link as LinkIcon, FileText, CheckCircle, Warning, Spinner, CaretLeft, CaretRight, Heartbeat, Bug, Envelope, PaperPlaneTilt, GitBranch, Star } from "@phosphor-icons/react";
import {
  getColleges, createCollege, deleteCollege,
  getCounsellingRounds, createOrUpdateRound,
  forceSeedData, getStats, formatFees, formatRound,
  ingestPdfUpload, ingestPdfUrl, getIngestionHistory, getRawAllotments, clearRawAllotments,
  classifySinglePdf, bulkStartIngestion, ingestSinglePdf,
  getIngestionJobStatus, detectPdfMetadata, getIngestionColleges,
  uploadSeatMatrix, uploadProvisionalSeatMatrix,
  ingestMccSeatMatrix, ingestMccAllotment,
  ingestMhSeatMatrix, ingestMhAllotment,
  ingestKeralaAllotment,
  ingestKeralaMerit,
  ingestTelanganaGovtAllotment, ingestTelanganaMgmtAllotment,
  ingestTelanganaGovtSeatMatrix, ingestTelanganaMgmtSeatMatrix,
  ingestAPGovtAllotment, ingestAPMgmtAllotment,
  ingestAPGovtSeatMatrix, ingestAPMgmtSeatMatrix,
  ingestWBAllotment, ingestWBSeatMatrix,
  ingestBiharAllotment, ingestBiharSeatMatrix,
  ingestHaryanaAllotment, ingestHaryanaSeatMatrix,
  ingestUPAllotment, ingestUPSeatMatrix,
  ingestRajasthanAllotment, ingestRajasthanSeatMatrix,
  ingestJharkhandAllotment, ingestJharkhandSeatMatrix,
  getFilterOptions
} from "../lib/api";
import BulkIngestion from "../components/BulkIngestion";
import NmcIngestion from "../components/NmcIngestion";
import NmcReview from "../components/NmcReview";
import AgentChat from "../components/AgentChat";

// Year options: current year down to 2024 (computed, so new years appear automatically).
const YEAR_OPTIONS = Array.from(
  { length: new Date().getFullYear() - 2023 },
  (_, i) => new Date().getFullYear() - i
);

// Canonical round label (matches backend ap_parser/kerala_parser encoding).
const roundLabel = (n) => {
  n = Number(n);
  if (n < 100) return `Round ${n}`;
  if (n < 200) return n === 100 ? "Stray Round" : `Stray R${n - 100}`;
  return `Special Stray R${n - 200}`;
};

export default function AdminPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "agent";
  const setActiveTab = (v) => setSearchParams((p) => { p.set("tab", v); return p; }, { replace: true });
  const [stats, setStats] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [roundsYear, setRoundsYear] = useState("2025");
  const [seeding, setSeeding] = useState(false);
  const [showAddCollege, setShowAddCollege] = useState(false);
  const [newCollege, setNewCollege] = useState({
    name: "", state: "", city: "", college_type: "govt",
    counselling_body: "MCC", annual_fees: 0, total_seats: 0,
    established_year: 2024, allows_other_domicile: true
  });

  // PDF Ingestion state
  const [uploadMode, setUploadMode] = useState("file"); // "file" or "url"
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [ingestYear, setIngestYear] = useState("2025");
  const [ingestRound, setIngestRound] = useState("1");
  const [ingestBody, setIngestBody] = useState("Karnataka");
  const [ingestState, setIngestState] = useState("Karnataka");
  const [ingesting, setIngesting] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [ingestError, setIngestError] = useState("");
  const [ingestionHistory, setIngestionHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [jobProgress, setJobProgress] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [autoDetected, setAutoDetected] = useState(null);
  const [detectedPdfType, setDetectedPdfType] = useState(null);
  const [provisionalSession, setProvisionalSession] = useState(null);

  // Raw allotments browser
  const [rawAllotments, setRawAllotments] = useState([]);
  const [rawTotal, setRawTotal] = useState(0);
  const [rawPage, setRawPage] = useState(1);
  const [rawFilters, setRawFilters] = useState({ counselling_body: "", year: "", round_number: "", college_name: "", course: "", category_raw: "", quota: "", min_rank: "", max_rank: "" });
  const [rawLoading, setRawLoading] = useState(false);
  const [quotasGrouped, setQuotasGrouped] = useState({});
  const [roundsGrouped, setRoundsGrouped] = useState({});
  const [bodiesFromData, setBodiesFromData] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [statsRes, collegesRes, roundsRes, historyRes] = await Promise.all([
        getStats(),
        getColleges({ limit: 200 }),
        getCounsellingRounds({}),
        getIngestionHistory().catch(() => ({ data: { history: [] } }))
      ]);
      setStats(statsRes.data);
      setColleges(collegesRes.data.colleges || []);
      setRounds(roundsRes.data.rounds || []);
      setIngestionHistory(historyRes.data.history || []);
      getFilterOptions().then(r => {
        setQuotasGrouped(r.data.quotas_grouped || {});
        setRoundsGrouped(r.data.rounds_grouped || {});
        setBodiesFromData(r.data.counselling_bodies || []);
      }).catch(() => {});
    } catch (e) {
      console.error("Failed to load admin data", e);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await forceSeedData();
      await loadAll();
    } catch (e) { console.error("Seed failed", e); }
    setSeeding(false);
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    setPdfFile(file);
    setAutoDetected(null);
    setDetectedPdfType(null);
    setProvisionalSession(null);
    setDetecting(true);
    setIngestError("");
    setDuplicateWarning(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await detectPdfMetadata(formData);
      const meta = res.data;
      const detected = {};
      if (meta.year) { setIngestYear(String(meta.year)); detected.year = true; }
      if (meta.round_number) { setIngestRound(String(meta.round_number)); detected.round = true; }
      if (meta.counselling_body) { setIngestBody(meta.counselling_body); detected.body = true; }
      if (meta.state) { setIngestState(meta.state); detected.state = true; }
      if (meta.pdf_type) { setDetectedPdfType(meta.pdf_type); }
      if (meta.provisional_session) { setProvisionalSession(meta.provisional_session); }
      if (meta.is_special_stray) { detected.is_special_stray = true; setIngestRound("201"); }
      setAutoDetected(detected);
      // Show duplicate warning if detected
      if (meta.duplicate) {
        setDuplicateWarning({ duplicate_type: meta.duplicate.type, message: meta.duplicate.message });
      }
    } catch (e) {
      console.error("Auto-detect failed, user can fill manually", e);
    }
    setDetecting(false);
  };

  const handleAddCollege = async () => {
    try {
      await createCollege(newCollege);
      setShowAddCollege(false);
      setNewCollege({ name: "", state: "", city: "", college_type: "govt", counselling_body: "MCC", annual_fees: 0, total_seats: 0, established_year: 2024, allows_other_domicile: true });
      const res = await getColleges({ limit: 200 });
      setColleges(res.data.colleges || []);
    } catch (e) { console.error("Failed to add college", e); }
  };

  const handleDeleteCollege = async (id) => {
    if (!window.confirm("Delete this college?")) return;
    try {
      await deleteCollege(id);
      setColleges(colleges.filter((c) => c.id !== id));
    } catch (e) { console.error("Failed to delete college", e); }
  };

  const handleUpdateRoundStatus = async (round, newStatus) => {
    try {
      await createOrUpdateRound({ year: round.year, round_number: round.round_number, counselling_body: round.counselling_body, status: newStatus });
      const res = await getCounsellingRounds(roundsYear !== "all" ? { year: parseInt(roundsYear) } : {});
      setRounds(res.data.rounds || []);
    } catch (e) { console.error("Failed to update round", e); }
  };

  const handleUpdateRoundDates = async (round, startDate, endDate) => {
    try {
      await createOrUpdateRound({ year: round.year, round_number: round.round_number, counselling_body: round.counselling_body, start_date: startDate, end_date: endDate });
      const res = await getCounsellingRounds(roundsYear !== "all" ? { year: parseInt(roundsYear) } : {});
      setRounds(res.data.rounds || []);
    } catch (e) { console.error("Failed to update dates", e); }
  };

  const loadRoundsByYear = async (yr) => {
    setRoundsYear(yr);
    try {
      const res = await getCounsellingRounds(yr !== "all" ? { year: parseInt(yr) } : {});
      setRounds(res.data.rounds || []);
    } catch (e) { console.error("Failed to load rounds", e); }
  };

  // PDF Ingestion handlers
  const pollJobStatus = async (jobId) => {
    // ~15 min cap: large merit lists (394 pages → write 34k rows → enrich →
    // re-derive cutoffs) can run several minutes on prod Atlas. The backend job
    // runs off the event loop and its progress is mirrored to Mongo, so polling
    // is reliable across workers; this is just the client's patience window.
    const maxPolls = 900;
    for (let i = 0; i < maxPolls; i++) {
      try {
        const res = await getIngestionJobStatus(jobId);
        const job = res.data;
        setJobProgress(job);

        if (job.status === "done" || job.status === "completed") {
          setIngestResult(job.result);
          setIngesting(false);
          setJobProgress(null);
          const [statsRes, historyRes] = await Promise.all([
            getStats(),
            getIngestionHistory().catch(() => ({ data: { history: [] } }))
          ]);
          setStats(statsRes.data);
          setIngestionHistory(historyRes.data.history || []);
          return;
        }

        if (job.status === "error" || job.status === "failed") {
          setIngestError(job.error || "Processing failed");
          setIngesting(false);
          setJobProgress(null);
          return;
        }

        await new Promise((r) => setTimeout(r, 1000));
      } catch (e) {
        console.error("Poll error", e);
        await new Promise((r) => setTimeout(r, 2000));
      }
    }
    setIngestError("Job timed out. Check ingestion history for partial results.");
    setIngesting(false);
    setJobProgress(null);
  };

  const handlePdfUpload = async (forceReplace = false) => {
    setIngesting(true);
    setIngestResult(null);
    setIngestError("");
    setJobProgress(null);
    setDuplicateWarning(null);

    try {
      let result;
      const isMCC = ingestBody === "MCC";
      const isMH = ingestBody === "Maharashtra";
      const pdfType = detectedPdfType;

      // ── Unified single-PDF ingestion (converged with Bulk / Crawler / Agent / Diary) ──
      // ONE call to /ingest/single/ingest, which runs classify_single + run_batch in the
      // SAME request/worker and dispatches through the common _dispatch_file. So every
      // body + doc-type (allotment / seat matrix / merit list) routes identically, we get
      // a dedicated parser + Firebase copy + ONE clean Ingestion Diary entry + granular
      // live progress, and the old two-call classify→start handoff (which broke across
      // prod workers with "Batch not found") is gone. The admin's Body/Year/Round/Type
      // selections are sent as overrides that win over auto-classification.
      if ((uploadMode === "file" && pdfFile) || (uploadMode === "url" && pdfUrl)) {
        const fd = new FormData();
        if (uploadMode === "file") fd.append("file", pdfFile);
        else { fd.append("pdf_url", pdfUrl); fd.append("source_url", pdfUrl); }
        fd.append("year", String(parseInt(ingestYear)));
        fd.append("body", ingestBody);
        if (ingestState) fd.append("state", ingestState);
        // pdf_type is passed with the SAME keys _dispatch_file expects (not "seat_matrix").
        if (pdfType === "full_seat_matrix") {
          fd.append("pdf_type", "full_seat_matrix");
        } else if (pdfType === "provisional_seat_matrix") {
          fd.append("pdf_type", "provisional_seat_matrix");
          fd.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
          fd.append("round_number", String(parseInt(provisionalSession || ingestRound)));
        } else if (pdfType === "stray_seat_matrix") {
          fd.append("pdf_type", "stray_seat_matrix");
          fd.append("matrix_type", autoDetected?.is_special_stray ? "stray_special" : "stray_r1");
        } else if (pdfType === "merit_list") {
          fd.append("pdf_type", "merit_list");
        } else {
          fd.append("pdf_type", "allotment");
          fd.append("round_number", String(parseInt(ingestRound)));
        }
        try {
          result = await ingestSinglePdf(fd);
        } catch (e) {
          setIngestError(e.response?.data?.detail || e.message || "Ingestion failed to start");
          setIngesting(false);
          return;
        }
        const jobId = result.data?.job_id;
        if (jobId) {
          setJobProgress({ status: "queued", progress: 0 });
          await pollJobStatus(jobId);
        } else {
          setIngestResult(result.data);
          setIngesting(false);
          getIngestionHistory().then(r => setIngestionHistory(r.data.history || [])).catch(() => {});
        }
        return;
      }

      if (uploadMode === "file" && pdfFile) {
        const formData = new FormData();
        formData.append("file", pdfFile);
        formData.append("year", ingestYear);
        
        if (pdfType === "full_seat_matrix") {
          // Route to seat matrix parser
          if (isMCC) {
            result = await ingestMccSeatMatrix(formData);
          } else if (isMH) {
            result = await ingestMhSeatMatrix(formData);
          } else if (ingestBody === "Telangana_Govt") {
            result = await ingestTelanganaGovtSeatMatrix(formData);
          } else if (ingestBody === "Telangana_Mgmt") {
            result = await ingestTelanganaMgmtSeatMatrix(formData);
          } else if (ingestBody === "AP_Govt") {
            result = await ingestAPGovtSeatMatrix(formData);
          } else if (ingestBody === "AP_Mgmt") {
            result = await ingestAPMgmtSeatMatrix(formData);
          } else if (ingestBody === "West_Bengal") {
            result = await ingestWBSeatMatrix(formData);
          } else if (ingestBody === "Bihar") {
            result = await ingestBiharSeatMatrix(formData);
          } else if (ingestBody === "Haryana") {
            result = await ingestHaryanaSeatMatrix(formData);
          } else if (ingestBody === "Uttar_Pradesh") {
            result = await ingestUPSeatMatrix(formData);
          } else if (ingestBody === "Rajasthan") {
            result = await ingestRajasthanSeatMatrix(formData);
          } else if (ingestBody === "Jharkhand") {
            result = await ingestJharkhandSeatMatrix(formData);
          } else {
            formData.append("counselling_body", ingestBody);
            formData.append("state", ingestState);
            result = await uploadSeatMatrix(formData);
          }
        } else if (pdfType === "provisional_seat_matrix") {
          // Route to provisional seat matrix parser
          if (isMCC) {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            formData.append("session", String(provisionalSession || ingestRound));
            result = await ingestMccSeatMatrix(formData);
          } else if (isMH) {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestMhSeatMatrix(formData);
          } else if (ingestBody === "Telangana_Govt") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestTelanganaGovtSeatMatrix(formData);
          } else if (ingestBody === "Telangana_Mgmt") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestTelanganaMgmtSeatMatrix(formData);
          } else if (ingestBody === "AP_Govt") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestAPGovtSeatMatrix(formData);
          } else if (ingestBody === "AP_Mgmt") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestAPMgmtSeatMatrix(formData);
          } else if (ingestBody === "West_Bengal") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestWBSeatMatrix(formData);
          } else if (ingestBody === "Bihar") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestBiharSeatMatrix(formData);
          } else if (ingestBody === "Haryana") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestHaryanaSeatMatrix(formData);
          } else if (ingestBody === "Uttar_Pradesh") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestUPSeatMatrix(formData);
          } else if (ingestBody === "Rajasthan") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestRajasthanSeatMatrix(formData);
          } else if (ingestBody === "Jharkhand") {
            formData.append("matrix_type", `provisional_r${provisionalSession || ingestRound}`);
            result = await ingestJharkhandSeatMatrix(formData);
          } else {
            formData.append("counselling_body", ingestBody);
            formData.append("state", ingestState);
            formData.append("session", String(provisionalSession || ingestRound));
            result = await uploadProvisionalSeatMatrix(formData);
          }
        } else if (pdfType === "stray_seat_matrix") {
          // Route to seat matrix parser with stray type
          const isSpecial = autoDetected?.is_special_stray;
          const strayType = isSpecial ? "stray_special" : "stray_r1";
          formData.append("matrix_type", strayType);
          if (isMCC) {
            result = await ingestMccSeatMatrix(formData);
          } else if (isMH) {
            result = await ingestMhSeatMatrix(formData);
          } else if (ingestBody === "Telangana_Govt") {
            result = await ingestTelanganaGovtSeatMatrix(formData);
          } else if (ingestBody === "Telangana_Mgmt") {
            result = await ingestTelanganaMgmtSeatMatrix(formData);
          } else if (ingestBody === "AP_Govt") {
            result = await ingestAPGovtSeatMatrix(formData);
          } else if (ingestBody === "AP_Mgmt") {
            result = await ingestAPMgmtSeatMatrix(formData);
          } else if (ingestBody === "West_Bengal") {
            result = await ingestWBSeatMatrix(formData);
          } else if (ingestBody === "Bihar") {
            result = await ingestBiharSeatMatrix(formData);
          } else if (ingestBody === "Haryana") {
            result = await ingestHaryanaSeatMatrix(formData);
          } else if (ingestBody === "Uttar_Pradesh") {
            result = await ingestUPSeatMatrix(formData);
          } else if (ingestBody === "Rajasthan") {
            result = await ingestRajasthanSeatMatrix(formData);
          } else if (ingestBody === "Jharkhand") {
            result = await ingestJharkhandSeatMatrix(formData);
          } else {
            formData.append("counselling_body", ingestBody);
            formData.append("state", ingestState);
            result = await uploadSeatMatrix(formData);
          }
        } else if (pdfType === "merit_list" && ingestBody === "Kerala") {
          // State merit / rank list → merit_list (true NEET AIR per appl no)
          result = await ingestKeralaMerit(formData);
        } else {
          // Allotment (default)
          formData.append("round_number", ingestRound);
          if (isMCC) {
            result = await ingestMccAllotment(formData);
          } else if (isMH) {
            result = await ingestMhAllotment(formData);
          } else if (ingestBody === "Kerala") {
            result = await ingestKeralaAllotment(formData);
          } else if (ingestBody === "Telangana_Govt") {
            result = await ingestTelanganaGovtAllotment(formData);
          } else if (ingestBody === "Telangana_Mgmt") {
            result = await ingestTelanganaMgmtAllotment(formData);
          } else if (ingestBody === "AP_Govt") {
            result = await ingestAPGovtAllotment(formData);
          } else if (ingestBody === "AP_Mgmt") {
            result = await ingestAPMgmtAllotment(formData);
          } else if (ingestBody === "West_Bengal") {
            result = await ingestWBAllotment(formData);
          } else if (ingestBody === "Bihar") {
            result = await ingestBiharAllotment(formData);
          } else if (ingestBody === "Haryana") {
            result = await ingestHaryanaAllotment(formData);
          } else if (ingestBody === "Uttar_Pradesh") {
            result = await ingestUPAllotment(formData);
          } else if (ingestBody === "Rajasthan") {
            result = await ingestRajasthanAllotment(formData);
          } else if (ingestBody === "Jharkhand") {
            result = await ingestJharkhandAllotment(formData);
          } else {
            formData.append("counselling_body", ingestBody);
            formData.append("state", ingestState);
            if (forceReplace) formData.append("force", "true");
            result = await ingestPdfUpload(formData);
          }
        }
      } else if (uploadMode === "url" && pdfUrl) {
        result = await ingestPdfUrl({
          url: pdfUrl,
          year: parseInt(ingestYear),
          round_number: parseInt(ingestRound),
          counselling_body: ingestBody,
          state: ingestState,
          force: forceReplace
        });
      } else {
        setIngestError("Please provide a PDF file or URL");
        setIngesting(false);
        return;
      }

      // Check for duplicate response
      if (result.data.duplicate) {
        setDuplicateWarning(result.data);
        setIngesting(false);
        return;
      }

      const jobId = result.data.job_id;
      if (jobId) {
        setJobProgress({ status: result.data.status || "queued", progress: 0 });
        await pollJobStatus(jobId);
      } else {
        setIngestResult(result.data);
        setIngesting(false);
        // Refresh history
        getIngestionHistory().then(r => setIngestionHistory(r.data.history || [])).catch(() => {});
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Ingestion failed";
      setIngestError(msg);
      setIngesting(false);
      setJobProgress(null);
    }
  };

  const loadRawAllotments = async (pg = 1) => {
    setRawLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (rawFilters.counselling_body) params.counselling_body = rawFilters.counselling_body;
      if (rawFilters.year) params.year = parseInt(rawFilters.year);
      if (rawFilters.round_number) params.round_number = parseInt(rawFilters.round_number);
      if (rawFilters.college_name) params.college_name = rawFilters.college_name;
      if (rawFilters.course) params.course = rawFilters.course;
      if (rawFilters.category_raw) params.category_raw = rawFilters.category_raw;
      if (rawFilters.quota) params.quota = rawFilters.quota;
      if (rawFilters.min_rank) params.min_rank = parseInt(rawFilters.min_rank);
      if (rawFilters.max_rank) params.max_rank = parseInt(rawFilters.max_rank);
      const res = await getRawAllotments(params);
      setRawAllotments(res.data.records || []);
      setRawTotal(res.data.total || 0);
      setRawPage(pg);
    } catch (e) { console.error("Failed to load allotments", e); }
    setRawLoading(false);
  };

  // Lazy-load raw allotments the first time that tab is opened (via sidebar/URL).
  useEffect(() => {
    if (activeTab === "allotments" && rawAllotments.length === 0) loadRawAllotments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const [clearingRaw, setClearingRaw] = useState(false);
  const handleClearRawAllotments = async () => {
    const { counselling_body, year, round_number } = rawFilters;
    if (!counselling_body || !year || !round_number) return;
    const label = roundLabel(parseInt(round_number));
    if (!window.confirm(`Delete ALL allotment records for ${counselling_body} · ${year} · ${label}?\n\nThis removes the raw allotments AND their derived cutoffs for this exact slot. This cannot be undone.`)) return;
    setClearingRaw(true);
    try {
      const res = await clearRawAllotments({ counselling_body, year: parseInt(year), round_number: parseInt(round_number) });
      const d = res.data || {};
      alert(`Cleared ${d.deleted_raw_allotments || 0} allotment rows and ${d.deleted_counselling_results || 0} cutoff rows for ${counselling_body} · ${year} · ${label}.`);
      await loadRawAllotments(1);
    } catch (e) {
      console.error("Failed to clear allotments", e);
      alert("Failed to clear records. Please try again.");
    }
    setClearingRaw(false);
  };

  const STATES_LIST = [
    "Andhra Pradesh", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat",
    "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
    "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab", "Rajasthan",
    "Tamil Nadu", "Telangana", "Uttar Pradesh", "Uttarakhand", "West Bengal",
    "Assam", "Jammu & Kashmir", "Puducherry"
  ];

  const COUNSELLING_BODIES = [
    "MCC", "Karnataka", "Tamil Nadu", "Maharashtra", "Uttar_Pradesh", "Bihar",
    "West_Bengal", "Rajasthan", "Madhya_Pradesh", "Kerala", "Gujarat",
    "Odisha", "Telangana_Govt", "Telangana_Mgmt", "AP_Govt", "AP_Mgmt", "Punjab", "Haryana",
    "Jharkhand", "Himachal Pradesh", "Delhi", "Uttarakhand"
  ];

  const filteredRounds = roundsYear === "all" ? rounds : rounds.filter((r) => String(r.year) === roundsYear);

  return (
    <div className="min-h-screen" data-testid="admin-panel-page">
      <button onClick={handleSeed} disabled={seeding} className="hidden" data-testid="seed-data-btn"></button>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

          {/* ─── AGENT CHAT TAB ─────────────────────────────────────────── */}
          <TabsContent value="agent" className="mt-0">
            <div className="border border-zinc-200 border-t-0">
              <AgentChat />
            </div>
          </TabsContent>

          {/* ─── PDF INGESTION TAB ─────────────────────────────────────── */}
          <TabsContent value="ingest" className="mt-0">
            <div className="grid grid-cols-12 gap-4">
              {/* Upload Form */}
              <div className="col-span-12 lg:col-span-7 border border-zinc-200 border-t-0" data-testid="ingest-form-panel">
                <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">UPLOAD ALLOTMENT / SEAT MATRIX PDF</span>
                </div>

                <div className="p-6 space-y-4">
                  {/* Mode Toggle */}
                  <div className="flex gap-0 border border-zinc-200 w-fit" data-testid="upload-mode-toggle">
                    <button
                      onClick={() => setUploadMode("file")}
                      className={`h-9 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors duration-150 ${
                        uploadMode === "file" ? "bg-[#6043EF] text-white" : "bg-white text-[#52525B] hover:bg-zinc-50"
                      }`}
                      data-testid="mode-file-btn"
                    >
                      <UploadSimple size={12} weight="bold" /> FILE UPLOAD
                    </button>
                    <button
                      onClick={() => setUploadMode("url")}
                      className={`h-9 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors duration-150 border-l border-zinc-200 ${
                        uploadMode === "url" ? "bg-[#6043EF] text-white" : "bg-white text-[#52525B] hover:bg-zinc-50"
                      }`}
                      data-testid="mode-url-btn"
                    >
                      <LinkIcon size={12} weight="bold" /> URL INGESTION
                    </button>
                  </div>

                  {/* File Upload */}
                  {uploadMode === "file" && (
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">PDF FILE</label>
                      <div
                        className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                          pdfFile ? "border-[#00C04B] bg-[#00C04B]/5" : "border-zinc-300 hover:border-[#6043EF]"
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="file-drop-zone"
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={(e) => handleFileSelect(e.target.files[0])}
                          data-testid="file-input"
                        />
                        {pdfFile ? (
                          <div className="flex items-center justify-center gap-2">
                            {detecting ? (
                              <>
                                <Spinner size={18} weight="bold" className="animate-spin text-[#6043EF]" />
                                <span className="font-['IBM_Plex_Mono'] text-sm text-[#6043EF]">Reading PDF header...</span>
                              </>
                            ) : (
                              <>
                                <FileText size={20} weight="bold" className="text-[#00C04B]" />
                                <span className="font-['IBM_Plex_Mono'] text-sm text-[#09090B]">{pdfFile.name}</span>
                                <span className="font-['IBM_Plex_Mono'] text-xs text-zinc-400">
                                  ({(pdfFile.size / (1024 * 1024)).toFixed(1)} MB)
                                </span>
                              </>
                            )}
                          </div>
                        ) : (
                          <div>
                            <UploadSimple size={24} className="text-zinc-400 mx-auto mb-2" />
                            <p className="text-sm text-[#52525B]">Click to select a PDF file</p>
                            <p className="text-[10px] text-zinc-400 mt-1">Counselling result allotment PDFs (max 50MB)</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* URL Input */}
                  {uploadMode === "url" && (
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">PDF URL</label>
                      <Input
                        type="url"
                        value={pdfUrl}
                        onChange={(e) => setPdfUrl(e.target.value)}
                        placeholder="https://counselling-body.gov.in/results/allotment.pdf"
                        className="border-zinc-300 font-['IBM_Plex_Mono'] text-sm focus:border-[#6043EF]"
                        data-testid="pdf-url-input"
                      />
                      <p className="text-[10px] text-zinc-400 mt-1">Direct link to the PDF hosted on the counselling body website</p>
                    </div>
                  )}

                  {/* Detecting indicator */}
                  {detecting && (
                    <div className="flex items-center gap-2 text-xs text-[#6043EF] font-medium animate-fade-in" data-testid="detecting-indicator">
                      <Spinner size={12} weight="bold" className="animate-spin" />
                      AUTO-DETECTING METADATA FROM PDF HEADER...
                    </div>
                  )}

                  {/* Auto-detected banner */}
                  {autoDetected && Object.keys(autoDetected).length > 0 && !detecting && (
                    <div className="border border-[#00C04B] bg-[#00C04B]/5 px-3 py-2 flex items-center gap-2 text-xs animate-fade-in" data-testid="auto-detected-banner">
                      <CheckCircle size={14} weight="bold" className="text-[#00C04B]" />
                      <span className="text-[#09090B]">
                        Auto-detected from PDF: {[
                          autoDetected.year && "Year",
                          autoDetected.round && "Round",
                          autoDetected.body && "Body",
                          autoDetected.state && "State"
                        ].filter(Boolean).join(", ")}
                      </span>
                      <span className="text-zinc-400 ml-auto">You can override below if needed</span>
                    </div>
                  )}

                  {/* Metadata Fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 flex items-center gap-1.5">
                        YEAR {autoDetected?.year && <span className="text-[10px] font-['IBM_Plex_Mono'] text-[#00C04B] normal-case tracking-normal">(detected)</span>}
                      </label>
                      <Select value={ingestYear} onValueChange={setIngestYear}>
                        <SelectTrigger className={`border-zinc-300 ${autoDetected?.year ? "border-[#00C04B]" : ""}`} data-testid="ingest-year-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {YEAR_OPTIONS.map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 flex items-center gap-1.5">
                        ROUND {autoDetected?.round && <span className="text-[10px] font-['IBM_Plex_Mono'] text-[#00C04B] normal-case tracking-normal">(detected)</span>}
                      </label>
                      <Select value={ingestRound} onValueChange={setIngestRound}>
                        <SelectTrigger className={`border-zinc-300 ${autoDetected?.round ? "border-[#00C04B]" : ""}`} data-testid="ingest-round-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7].map((r) => (
                            <SelectItem key={r} value={String(r)}>Round {r}</SelectItem>
                          ))}
                          {[101, 102, 103].map((r) => (
                            <SelectItem key={r} value={String(r)}>Stray R{r - 100}</SelectItem>
                          ))}
                          <SelectItem value="201">Special Stray Round</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 flex items-center gap-1.5">
                        COUNSELLING BODY {autoDetected?.body && <span className="text-[10px] font-['IBM_Plex_Mono'] text-[#00C04B] normal-case tracking-normal">(detected)</span>}
                      </label>
                      <Select value={ingestBody} onValueChange={setIngestBody}>
                        <SelectTrigger className={`border-zinc-300 ${autoDetected?.body ? "border-[#00C04B]" : ""}`} data-testid="ingest-body-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNSELLING_BODIES.map((b) => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 flex items-center gap-1.5">
                        STATE {autoDetected?.state && <span className="text-[10px] font-['IBM_Plex_Mono'] text-[#00C04B] normal-case tracking-normal">(detected)</span>}
                      </label>
                      <Select value={ingestState} onValueChange={setIngestState}>
                        <SelectTrigger className={`border-zinc-300 ${autoDetected?.state ? "border-[#00C04B]" : ""}`} data-testid="ingest-state-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATES_LIST.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Submit Button OR Duplicate Warning OR Completion */}
                  {ingestResult ? (
                    <div className="flex gap-2 animate-fade-in">
                      <div className="flex-1 h-11 bg-[#00C04B] text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2">
                        <CheckCircle size={14} weight="bold" /> INGESTION COMPLETED
                      </div>
                      <button
                        onClick={() => { setIngestResult(null); setDuplicateWarning(null); setPdfFile(null); setPdfUrl(""); setAutoDetected(null); setIngestError(""); setJobProgress(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                        className="flex-1 h-11 border border-zinc-300 text-[#52525B] font-bold text-xs uppercase tracking-wider hover:bg-zinc-50 transition-colors duration-150"
                        data-testid="refresh-form-btn"
                      >
                        REFRESH
                      </button>
                    </div>
                  ) : duplicateWarning ? (
                    <div className="space-y-3 animate-fade-in">
                      <div className="border border-[#FFC107] bg-[#FFC107]/10 p-4" data-testid="duplicate-warning">
                        <div className="flex items-center gap-2 mb-2">
                          <Warning size={16} weight="bold" className="text-[#92700C]" />
                          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#92700C]">
                            {duplicateWarning.duplicate_type === "exact_file" ? "EXACT DUPLICATE FILE" : "DATA ALREADY EXISTS"}
                          </span>
                        </div>
                        <p className="text-sm text-[#09090B] font-sans">{duplicateWarning.message}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePdfUpload(true)}
                          disabled={ingesting}
                          className="flex-1 h-11 bg-[#FFC107] text-[#09090B] font-bold text-xs uppercase tracking-wider hover:bg-[#e6af06] transition-colors duration-150 disabled:opacity-50 flex items-center justify-center gap-2"
                          data-testid="force-replace-btn"
                        >
                          {ingesting ? <><Spinner size={14} weight="bold" className="animate-spin" /> PROCESSING...</> : "RE-INGEST (UPSERT)"}
                        </button>
                        <button
                          onClick={() => { setDuplicateWarning(null); setPdfFile(null); setPdfUrl(""); setAutoDetected(null); setIngestResult(null); setIngestError(""); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                          className="flex-1 h-11 border border-zinc-300 text-[#52525B] font-bold text-xs uppercase tracking-wider hover:bg-zinc-50 transition-colors duration-150"
                          data-testid="cancel-replace-btn"
                        >
                          CANCEL
                        </button>
                      </div>
                    </div>
                  ) : (
                    <IngestButton
                      pdfType={detectedPdfType}
                      provisionalSession={provisionalSession}
                      isSpecialStray={autoDetected?.is_special_stray}
                      ingesting={ingesting}
                      disabled={(uploadMode === "file" && !pdfFile) || (uploadMode === "url" && !pdfUrl)}
                      onClick={handlePdfUpload}
                    />
                  )}

                  {/* Live Progress */}
                  {jobProgress && (
                    <div className="border border-[#6043EF] bg-[#6043EF]/5 p-4 animate-fade-in" data-testid="ingest-progress">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6043EF]">
                          {jobProgress.status === "downloading" ? "DOWNLOADING PDF..." :
                           jobProgress.status === "parsing" ? "PARSING PDF PAGES..." :
                           jobProgress.status === "ingesting" ? "WRITING TO DATABASE..." :
                           "QUEUED..."}
                        </span>
                        <span className="font-['IBM_Plex_Mono'] text-sm font-bold text-[#6043EF]">{jobProgress.progress || 0}%</span>
                      </div>
                      {/* Progress Bar */}
                      <div className="w-full h-2 bg-zinc-200 mb-3">
                        <div
                          className="h-full bg-[#6043EF] transition-all duration-500 ease-out"
                          style={{ width: `${jobProgress.progress || 0}%` }}
                        />
                      </div>
                      <div className="flex gap-6 text-xs text-[#52525B]">
                        {jobProgress.total_pages > 0 && (
                          <span>Pages: <span className="font-['IBM_Plex_Mono'] font-bold text-[#09090B]">{jobProgress.pages_done}/{jobProgress.total_pages}</span></span>
                        )}
                        {jobProgress.records_parsed > 0 && (
                          <span>Records parsed: <span className="font-['IBM_Plex_Mono'] font-bold text-[#09090B]">{jobProgress.records_parsed.toLocaleString()}</span></span>
                        )}
                        {jobProgress.records_ingested > 0 && (
                          <span>Ingested: <span className="font-['IBM_Plex_Mono'] font-bold text-[#09090B]">{jobProgress.records_ingested.toLocaleString()}</span></span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Result */}
                  {ingestResult && (
                    <div className="border border-[#00C04B] bg-[#00C04B]/5 p-4 animate-fade-in" data-testid="ingest-result">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle size={16} weight="bold" className="text-[#00C04B]" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C04B]">INGESTION SUCCESSFUL</span>
                        {ingestResult.matrix_type && (
                          <span className="text-[10px] font-['IBM_Plex_Mono'] font-bold text-[#52525B] border border-zinc-300 px-2 py-0.5">
                            {ingestResult.matrix_type === "full" ? "FULL SEAT MATRIX" : ingestResult.matrix_type?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {/* Allotment fields */}
                        {ingestResult.total_records_parsed != null && <ResultStat label="Records Parsed" value={ingestResult.total_records_parsed} />}
                        {ingestResult.unique_colleges_found != null && <ResultStat label="Unique Colleges" value={ingestResult.unique_colleges_found} />}
                        {ingestResult.new_colleges_created != null && <ResultStat label="New Colleges" value={ingestResult.new_colleges_created} />}
                        {ingestResult.closing_rank_results_created != null && <ResultStat label="Closing Ranks" value={ingestResult.closing_rank_results_created} />}
                        {ingestResult.raw_allotments_stored != null && <ResultStat label="Raw Allotments" value={ingestResult.raw_allotments_stored} />}
                        {/* Seat matrix fields */}
                        {ingestResult.total_colleges != null && <ResultStat label="Total Colleges" value={ingestResult.total_colleges} />}
                        {ingestResult.new_colleges != null && <ResultStat label="New Colleges" value={ingestResult.new_colleges} />}
                        {ingestResult.updated_colleges != null && <ResultStat label="Updated Colleges" value={ingestResult.updated_colleges} />}
                        {ingestResult.seat_matrices_created != null && <ResultStat label="Seat Matrices Created" value={ingestResult.seat_matrices_created} />}
                        {ingestResult.detected_format && <ResultStat label="Format" value={ingestResult.detected_format} isText />}
                        {/* Common */}
                        {ingestResult.filename && <ResultStat label="File" value={ingestResult.filename} isText />}
                      </div>
                    </div>
                  )}

                  {/* Error */}
                  {ingestError && (
                    <div className="border border-[#FF2A00] bg-[#FF2A00]/5 p-4 animate-fade-in" data-testid="ingest-error">
                      <div className="flex items-center gap-2">
                        <Warning size={16} weight="bold" className="text-[#FF2A00]" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2A00]">ERROR</span>
                      </div>
                      <p className="text-sm text-[#09090B] mt-2 font-sans">{ingestError}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Ingestion History */}
              <div className="col-span-12 lg:col-span-5 border border-zinc-200 border-t-0" data-testid="ingest-history-panel">
                <div className="p-3 bg-[#F4F4F5] border-b border-zinc-200 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">INGESTION HISTORY</span>
                  {ingestionHistory.length > 10 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                        disabled={historyPage === 1}
                        className="w-6 h-6 flex items-center justify-center text-[#52525B] hover:text-[#09090B] disabled:opacity-30 transition-colors"
                        data-testid="history-prev"
                      >
                        <CaretLeft size={12} weight="bold" />
                      </button>
                      <span className="font-['IBM_Plex_Mono'] text-[10px] text-[#52525B]">
                        {historyPage}/{Math.ceil(ingestionHistory.length / 10)}
                      </span>
                      <button
                        onClick={() => setHistoryPage(Math.min(Math.ceil(ingestionHistory.length / 10), historyPage + 1))}
                        disabled={historyPage >= Math.ceil(ingestionHistory.length / 10)}
                        className="w-6 h-6 flex items-center justify-center text-[#52525B] hover:text-[#09090B] disabled:opacity-30 transition-colors"
                        data-testid="history-next"
                      >
                        <CaretRight size={12} weight="bold" />
                      </button>
                    </div>
                  )}
                </div>
                {ingestionHistory.length > 0 ? (
                  <div>
                    {ingestionHistory.slice((historyPage - 1) * 10, historyPage * 10).map((h, i) => (
                      <HistoryRow key={`${historyPage}-${i}`} h={h} i={(historyPage - 1) * 10 + i} />
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <FileText size={32} className="text-zinc-300 mx-auto mb-3" />
                    <p className="text-sm text-[#52525B]">No PDFs ingested yet</p>
                    <p className="text-[10px] text-zinc-400 mt-1">Upload a counselling result PDF to get started</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ─── BULK ZIP INGESTION TAB ──────────────────────────────────── */}
          <TabsContent value="bulk" className="mt-0">
            <BulkIngestion />
          </TabsContent>


          {/* ─── RAW ALLOTMENTS TAB ────────────────────────────────────── */}
          <TabsContent value="allotments" className="mt-0">
            <div className="border border-zinc-200 border-t-0">
              <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200 flex items-center gap-3 flex-wrap">
                <Input
                  placeholder="Search college..."
                  value={rawFilters.college_name}
                  onChange={(e) => setRawFilters({ ...rawFilters, college_name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") loadRawAllotments(1); }}
                  className="w-48 h-8 border-zinc-300 text-xs"
                  data-testid="raw-search-input"
                />
                <Select value={rawFilters.counselling_body || "all"} onValueChange={(v) => setRawFilters({ ...rawFilters, counselling_body: v === "all" ? "" : v, quota: "" })}>
                  <SelectTrigger className="w-36 h-8 border-zinc-300 text-xs" data-testid="raw-body-filter">
                    <SelectValue placeholder="All Bodies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Bodies</SelectItem>
                    {(bodiesFromData.length ? bodiesFromData : COUNSELLING_BODIES).map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={rawFilters.year || "all"} onValueChange={(v) => setRawFilters({ ...rawFilters, year: v === "all" ? "" : v })}>
                  <SelectTrigger className="w-24 h-8 border-zinc-300 text-xs" data-testid="raw-year-filter">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {YEAR_OPTIONS.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={rawFilters.round_number || "all"} onValueChange={(v) => setRawFilters({ ...rawFilters, round_number: v === "all" ? "" : v })}>
                  <SelectTrigger className="w-28 h-8 border-zinc-300 text-xs" data-testid="raw-round-filter">
                    <SelectValue placeholder="Round" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Rounds</SelectItem>
                    {(() => {
                      const bodyRounds = rawFilters.counselling_body ? (roundsGrouped[rawFilters.counselling_body] || []) : null;
                      const list = (bodyRounds && bodyRounds.length > 0)
                        ? bodyRounds
                        : [1, 2, 3, 4, 5, 6, 7, 100, 101, 102, 103, 201, 202, 203];
                      return list.map((r) => (
                        <SelectItem key={r} value={String(r)}>{roundLabel(r)}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                <Select value={rawFilters.course || "all"} onValueChange={(v) => setRawFilters({ ...rawFilters, course: v === "all" ? "" : v })}>
                  <SelectTrigger className="w-32 h-8 border-zinc-300 text-xs" data-testid="raw-course-filter">
                    <SelectValue placeholder="Course" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Courses</SelectItem>
                    {["MBBS", "BDS", "BHMS", "BSc_Nursing", "BVSc"].map((c) => (
                      <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Raw category (e.g. GMPH, 2AG)"
                  value={rawFilters.category_raw}
                  onChange={(e) => setRawFilters({ ...rawFilters, category_raw: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") loadRawAllotments(1); }}
                  className="w-44 h-8 border-zinc-300 text-xs"
                  data-testid="raw-category-input"
                />
                {(() => {
                  const bodyQuotas = rawFilters.counselling_body ? (quotasGrouped[rawFilters.counselling_body] || []) : [];
                  if (bodyQuotas.length === 0) return null;
                  return (
                    <Select value={rawFilters.quota || "all"} onValueChange={(v) => setRawFilters({ ...rawFilters, quota: v === "all" ? "" : v })}>
                      <SelectTrigger className="w-44 h-8 border-zinc-300 text-xs" data-testid="raw-quota-filter">
                        <SelectValue placeholder="All Quotas" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="all">All Quotas ({bodyQuotas.length})</SelectItem>
                        {bodyQuotas.map((q) => (
                          <SelectItem key={q} value={q}>{q}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
                <Input
                  type="number"
                  placeholder="Min rank"
                  value={rawFilters.min_rank}
                  onChange={(e) => setRawFilters({ ...rawFilters, min_rank: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") loadRawAllotments(1); }}
                  className="w-28 h-8 border-zinc-300 text-xs"
                  data-testid="raw-min-rank-input"
                />
                <Input
                  type="number"
                  placeholder="Max rank"
                  value={rawFilters.max_rank}
                  onChange={(e) => setRawFilters({ ...rawFilters, max_rank: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") loadRawAllotments(1); }}
                  className="w-28 h-8 border-zinc-300 text-xs"
                  data-testid="raw-max-rank-input"
                />
                <button
                  onClick={() => loadRawAllotments(1)}
                  className="h-8 px-4 bg-[#6043EF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#4A28C2]"
                  data-testid="raw-search-btn"
                >
                  SEARCH
                </button>
                {rawFilters.counselling_body && rawFilters.year && rawFilters.round_number && (
                  <button
                    onClick={handleClearRawAllotments}
                    disabled={clearingRaw}
                    className="h-8 px-3 border border-rose-300 text-rose-600 text-[10px] font-bold uppercase tracking-wider hover:bg-rose-50 disabled:opacity-50 flex items-center gap-1"
                    data-testid="raw-clear-btn"
                    title="Delete all allotment + derived cutoff records for the selected body, year and round">
                    <Trash size={12} weight="bold" /> {clearingRaw ? "Clearing…" : "Clear records"}
                  </button>
                )}
                <span className="ml-auto font-['IBM_Plex_Mono'] text-xs text-[#52525B]">{rawTotal.toLocaleString()} records</span>
              </div>
              {rawLoading ? (
                <div className="p-8 text-center"><Spinner size={24} className="animate-spin text-zinc-400 mx-auto" /></div>
              ) : rawAllotments.length > 0 ? (
                <>
                  <div className="overflow-x-auto" data-testid="raw-allotments-table">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right w-12">#</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">AIR</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">RND</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">CODE</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] min-w-[200px]">COLLEGE</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">COURSE</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">QUOTA</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">CAT (RAW)</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">CAT (NORM)</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">FEES</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">BODY</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rawAllotments.map((r, i) => (
                          <TableRow key={`${r.id}-${i}`} className="hover:bg-zinc-50" data-testid={`raw-row-${i}`}>
                            <TableCell className="text-right font-['IBM_Plex_Mono'] text-xs text-zinc-400" data-testid={`raw-serial-${i}`}>{(rawPage - 1) * 50 + i + 1}</TableCell>
                            <TableCell className="text-right font-['IBM_Plex_Mono'] font-semibold text-sm">{r.all_india_rank?.toLocaleString()}</TableCell>
                            <TableCell className="font-['IBM_Plex_Mono'] text-xs text-center">{formatRound(r.round_number)}</TableCell>
                            <TableCell className="font-['IBM_Plex_Mono'] text-xs">
                              <span className="text-[#09090B]">{r.college_code || "—"}</span>
                              {r.course_code && (
                                <span className="block text-[10px] text-zinc-400" title="raw source code from PDF">{r.course_code}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs font-medium">
                              <div>{r.college_name}</div>
                              {r.college_name_raw && r.college_name_raw !== r.college_name && (
                                <div className="text-[10px] font-normal text-zinc-400 truncate max-w-[260px]" title={r.college_name_raw}>
                                  {r.college_name_raw}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-[#52525B]">{r.course}</TableCell>
                            <TableCell className="text-xs text-[#52525B]">{r.quota || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{r.category_raw}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-[#6043EF] text-white text-[10px] uppercase tracking-wider">{r.category_normalized}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-['IBM_Plex_Mono'] text-xs">{r.fees?.toLocaleString()}</TableCell>
                            <TableCell className="text-xs text-[#52525B]">{r.counselling_body}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Pagination */}
                  {rawTotal > 50 && (
                    <div className="flex items-center justify-between p-3 border-t border-zinc-200">
                      <button onClick={() => loadRawAllotments(Math.max(1, rawPage - 1))} disabled={rawPage === 1}
                        className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#52525B] disabled:opacity-30" data-testid="raw-prev-btn">
                        <CaretLeft size={12} /> PREV
                      </button>
                      <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]">Page {rawPage} of {Math.ceil(rawTotal / 50)}</span>
                      <button onClick={() => loadRawAllotments(rawPage + 1)} disabled={rawPage >= Math.ceil(rawTotal / 50)}
                        className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#52525B] disabled:opacity-30" data-testid="raw-next-btn">
                        NEXT <CaretRight size={12} />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-8 text-center">
                  <FileText size={32} className="text-zinc-300 mx-auto mb-3" />
                  <p className="text-sm text-[#52525B]">No raw allotment records found. Ingest a PDF first.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─── MANAGE COLLEGES TAB ───────────────────────────────────── */}
          <TabsContent value="colleges" className="mt-0">
            {/* NMC Seat Matrix Ingestion */}
            <NmcIngestion />
            
            <div className="border border-zinc-200 border-t-0">
              <div className="p-3 bg-[#F4F4F5] border-b border-zinc-200 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">COLLEGES ({colleges.length})</span>
                <button onClick={() => setShowAddCollege(!showAddCollege)}
                  className="h-7 px-3 bg-[#6043EF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] transition-colors flex items-center gap-1"
                  data-testid="toggle-add-college-btn">
                  <Plus size={10} weight="bold" /> ADD COLLEGE
                </button>
              </div>
              {showAddCollege && (
                <div className="p-4 border-b border-zinc-200 bg-zinc-50" data-testid="add-college-form">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                    <Input placeholder="College Name" value={newCollege.name} onChange={(e) => setNewCollege({ ...newCollege, name: e.target.value })} className="border-zinc-300 col-span-2" data-testid="new-college-name" />
                    <Input placeholder="City" value={newCollege.city} onChange={(e) => setNewCollege({ ...newCollege, city: e.target.value })} className="border-zinc-300" data-testid="new-college-city" />
                    <Input placeholder="State" value={newCollege.state} onChange={(e) => setNewCollege({ ...newCollege, state: e.target.value })} className="border-zinc-300" data-testid="new-college-state" />
                    <Select value={newCollege.college_type} onValueChange={(v) => setNewCollege({ ...newCollege, college_type: v })}>
                      <SelectTrigger className="border-zinc-300" data-testid="new-college-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="govt">Government</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="deemed">Deemed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Counselling Body" value={newCollege.counselling_body} onChange={(e) => setNewCollege({ ...newCollege, counselling_body: e.target.value })} className="border-zinc-300" data-testid="new-college-body" />
                    <Input type="number" placeholder="Annual Fees" value={newCollege.annual_fees || ""} onChange={(e) => setNewCollege({ ...newCollege, annual_fees: parseInt(e.target.value) || 0 })} className="border-zinc-300 font-['IBM_Plex_Mono']" data-testid="new-college-fees" />
                    <Input type="number" placeholder="Total Seats" value={newCollege.total_seats || ""} onChange={(e) => setNewCollege({ ...newCollege, total_seats: parseInt(e.target.value) || 0 })} className="border-zinc-300 font-['IBM_Plex_Mono']" data-testid="new-college-seats" />
                  </div>
                  <button onClick={handleAddCollege} disabled={!newCollege.name || !newCollege.state || !newCollege.city}
                    className="h-8 px-4 bg-[#6043EF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] disabled:opacity-50"
                    data-testid="submit-add-college-btn">ADD COLLEGE</button>
                </div>
              )}
              <div className="overflow-x-auto" data-testid="admin-colleges-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] min-w-[250px]">COLLEGE</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">STATE</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">TYPE</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">BODY</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">SEATS</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">FEES</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {colleges.map((c, i) => (
                      <TableRow key={c.id} className="hover:bg-zinc-50" data-testid={`admin-college-row-${i}`}>
                        <TableCell className="font-medium text-sm">{c.name}<span className="block text-[10px] text-zinc-400">{c.city}</span></TableCell>
                        <TableCell className="text-xs text-[#52525B]">{c.state}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px] uppercase">{c.college_type}</Badge></TableCell>
                        <TableCell className="text-xs text-[#52525B]">{c.counselling_body}</TableCell>
                        <TableCell className="text-right font-['IBM_Plex_Mono'] text-sm">{c.total_seats}</TableCell>
                        <TableCell className="text-right font-['IBM_Plex_Mono'] text-sm">{formatFees(c.annual_fees)}</TableCell>
                        <TableCell>
                          <button onClick={() => handleDeleteCollege(c.id)} className="text-zinc-400 hover:text-[#FF2A00] transition-colors" data-testid={`delete-college-${i}`}>
                            <Trash size={14} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* ─── REVIEW COLLEGES TAB ──────────────────────────────────── */}
          <TabsContent value="review-colleges" className="mt-0">
            <NmcReview />
          </TabsContent>

          {/* ─── MANAGE ROUNDS TAB ─────────────────────────────────────── */}
          <TabsContent value="rounds" className="mt-0">
            <div className="border border-zinc-200 border-t-0">
              <div className="p-3 bg-[#F4F4F5] border-b border-zinc-200 flex items-center gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">COUNSELLING ROUNDS</span>
                <Select value={roundsYear} onValueChange={loadRoundsByYear}>
                  <SelectTrigger className="w-28 h-7 border-zinc-300 text-xs" data-testid="rounds-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {YEAR_OPTIONS.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="ml-auto font-['IBM_Plex_Mono'] text-xs text-[#52525B]">{filteredRounds.length} rounds</span>
              </div>
              <div className="overflow-x-auto" data-testid="admin-rounds-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">BODY</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">YEAR</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">ROUND</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">STATUS</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">DATE RANGE</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">PUB. DATE</TableHead>
                      <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">ACTIONS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRounds.map((r, i) => (
                      <TableRow key={r.id || i} className="hover:bg-zinc-50" data-testid={`admin-round-row-${i}`}>
                        <TableCell className="text-sm font-medium">{r.counselling_body}</TableCell>
                        <TableCell className="font-['IBM_Plex_Mono'] text-sm">{r.year}</TableCell>
                        <TableCell className="font-['IBM_Plex_Mono'] text-sm">{formatRound(r.round_number)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${
                            r.status === "completed" ? "border-[#00C04B] text-[#00C04B]" :
                            r.status === "ongoing" ? "border-[#FFC107] text-[#92700C]" :
                            r.status === "awaiting_result" ? "border-[#6043EF] text-[#6043EF]" :
                            "border-zinc-300 text-[#52525B]"
                          }`}>{r.status === "awaiting_result" ? "AWAITING RESULT" : r.status}</Badge>
                        </TableCell>
                        <TableCell className="font-['IBM_Plex_Mono'] text-xs text-zinc-400">
                          {r.start_date && r.end_date ? `${r.start_date} — ${r.end_date}` : "—"}
                        </TableCell>
                        <TableCell className="font-['IBM_Plex_Mono'] text-xs text-[#6043EF] font-medium">
                          {r.publication_date || "—"}
                        </TableCell>
                        <TableCell>
                          <Select value={r.status} onValueChange={(v) => handleUpdateRoundStatus(r, v)}>
                            <SelectTrigger className="h-7 w-36 text-[10px] border-zinc-300" data-testid={`round-status-select-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="upcoming">Upcoming</SelectItem>
                              <SelectItem value="ongoing">Ongoing</SelectItem>
                              <SelectItem value="awaiting_result">Awaiting Result</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredRounds.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-sm text-[#52525B]">
                          No counselling rounds found for the selected year
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ResultStat({ label, value, isText }) {
  return (
    <div className="border border-zinc-200 p-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] block mb-1">{label}</span>
      <span className={`${isText ? "text-xs" : "text-lg font-bold"} font-['IBM_Plex_Mono'] text-[#09090B]`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </span>
    </div>
  );
}


function HistoryRow({ h, i }) {
  const [expanded, setExpanded] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const uniqueColleges = h.unique_colleges || h.colleges_created || h.colleges || 0;
  const newCount = h.new_colleges_count || h.colleges_created || 0;
  const newNames = h.new_college_names || [];

  return (
    <div className="border-b border-zinc-200" data-testid={`history-row-${i}`}>
      {/* Summary row - clickable */}
      <button
        onClick={() => { setExpanded(!expanded); if (expanded) setShowNew(false); }}
        className="w-full p-3 flex items-center gap-3 text-left hover:bg-zinc-50 transition-colors duration-150"
        data-testid={`history-toggle-${i}`}
      >
        <CaretRight size={12} weight="bold" className={`text-zinc-400 transition-transform duration-150 flex-shrink-0 ${expanded ? "rotate-90" : ""}`} />
        <span className="text-xs font-medium text-[#09090B] w-24">{h.counselling_body}</span>
        <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B] w-10">{h.year}</span>
        <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B] w-24">{h.log_type || formatRound(h.round_number)}</span>
        <span className="font-['IBM_Plex_Mono'] text-xs font-bold text-[#09090B] w-14 text-right">{(h.records_count || h.records)?.toLocaleString() || "—"}</span>
        <span className="font-['IBM_Plex_Mono'] text-[10px] text-[#6043EF] font-medium w-20">{h.publication_date || "—"}</span>
        <span className="font-['IBM_Plex_Mono'] text-[10px] text-zinc-400 ml-auto">
          {h.ingested_at ? new Date(h.ingested_at).toLocaleString() : "—"}
        </span>
      </button>

      {/* Expanded: colleges summary + show new */}
      {expanded && (
        <div className="px-3 pb-3 pt-0 animate-fade-in">
          <div className="flex items-center gap-2 text-[10px] ml-5">
            <span className="text-[#52525B]">
              Unique colleges: <span className="font-['IBM_Plex_Mono'] font-bold text-[#09090B]">{uniqueColleges}</span>
            </span>
            <span className="text-zinc-300">|</span>
            <span className="text-[#52525B]">
              New: <span className="font-['IBM_Plex_Mono'] font-bold text-[#6043EF]">{newCount}</span>
            </span>
            {newCount > 0 && (
              <>
                <span className="text-zinc-300">|</span>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowNew(!showNew); }}
                  className="text-[#6043EF] font-bold uppercase hover:underline"
                  data-testid={`show-new-btn-${i}`}
                >
                  {showNew ? "HIDE NEW" : "SHOW NEW"}
                </button>
              </>
            )}
          </div>

          {/* New colleges list */}
          {showNew && newNames.length > 0 && (
            <div className="ml-5 mt-2 border border-[#6043EF]/20 bg-[#6043EF]/5 p-2 animate-fade-in" data-testid={`new-colleges-list-${i}`}>
              <div className="flex flex-wrap gap-1.5">
                {newNames.map((name, ni) => (
                  <span key={ni} className="text-[10px] font-['IBM_Plex_Mono'] text-[#09090B] border border-[#6043EF]/20 bg-white px-1.5 py-0.5">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}




function IngestButton({ pdfType, provisionalSession, isSpecialStray, ingesting, disabled, onClick }) {
  const config = {
    allotment: {
      bg: "bg-[#6043EF] hover:bg-[#4A28C2]",
      text: "text-white",
      label: "INGEST ALLOTMENT DATA",
      icon: "upload",
    },
    full_seat_matrix: {
      bg: "bg-[#FFC107] hover:bg-[#e6af06]",
      text: "text-[#09090B]",
      label: "INGEST BASE SEAT MATRIX",
      icon: "table",
    },
    provisional_seat_matrix: {
      bg: "bg-[#FF2A00] hover:bg-[#cc2200]",
      text: "text-white",
      label: provisionalSession
        ? `INGEST PROVISIONAL SEAT MATRIX (R${provisionalSession})`
        : "INGEST PROVISIONAL SEAT MATRIX",
      icon: "table",
    },
    stray_seat_matrix: {
      bg: "bg-[#7C3AED] hover:bg-[#6D28D9]",
      text: "text-white",
      label: isSpecialStray ? "INGEST ADD. STRAY SEAT MATRIX" : "INGEST STRAY SEAT MATRIX",
      icon: "table",
    },
    merit_list: {
      bg: "bg-[#0EA5E9] hover:bg-[#0284C7]",
      text: "text-white",
      label: "INGEST MERIT / RANK LIST (NEET AIR)",
      icon: "upload",
    },
  };

  const c = config[pdfType] || config.allotment;

  return (
    <div className="space-y-2">
      <button
        onClick={() => onClick()}
        disabled={ingesting || disabled}
        className={`w-full h-11 ${c.bg} ${c.text} font-bold text-xs uppercase tracking-wider transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
        data-testid="ingest-submit-btn"
      >
        {ingesting ? (
          <><Spinner size={14} weight="bold" className="animate-spin" /> PROCESSING...</>
        ) : (
          <>{c.icon === "table" ? <FileText size={14} weight="bold" /> : <UploadSimple size={14} weight="bold" />} {c.label}</>
        )}
      </button>
      {pdfType?.includes("seat_matrix") && (
        <p className="text-[10px] text-[#52525B] text-center font-['IBM_Plex_Mono']">
          Detected as {pdfType === "full_seat_matrix" ? "full" : "provisional"} seat matrix — will route to seat matrix parser
        </p>
      )}
    </div>
  );
}
